import React from "react";
import { format } from "d3-format";
import { useQuery } from "@apollo/client";

import {
  formatBytes,
  formatNumber,
  formatBytesTrend,
  formatBytesTrendDetail,
} from "utils/format";
import {
  SchemaTableSummaryStatsType,
  SchemaTablePartialSummaryStatsType,
} from "types/SchemaTable";

import Panel from "ui/Panel";
import PanelSection from "ui/PanelSection";
import PanelTable from "ui/PanelTable";
import Badge from "ui/Badge";

import styles from "./style.module.scss";
import { AreaSeries, LineSeries } from "ui/Graph/Series";
import { Data } from "ui/Graph/util";
import { useDateRange } from "providers/WithDateRange";

import {
  SchemaTableStats as SchemaTableStatsType,
  SchemaTableStatsVariables,
} from "./types/SchemaTableStats";
import QUERY from "./Query.graphql";
import GraphSection from "ui/Graph/GraphSection";
import DateRangeGraph from "ui/Graph/DateRangeGraph";

type Props = {
  databaseId: string;
  fillfactor: number | null | undefined;
  stats?: SchemaTableSummaryStatsType;
  tableId: string;
  partitionedBy?: string;
  stats7dAgo?: SchemaTablePartialSummaryStatsType;
  materializedView?: boolean | null | undefined;
};

const SchemaTableStats: React.FunctionComponent<Props> = ({
  databaseId,
  tableId,
  partitionedBy,
  fillfactor,
  materializedView,
  stats,
  stats7dAgo,
}) => {
  const { from, to } = useDateRange();

  const { data, loading, error } = useQuery<
    SchemaTableStatsType,
    SchemaTableStatsVariables
  >(QUERY, {
    variables: {
      databaseId,
      tableId,
      startTs: from.unix(),
      endTs: to.unix(),
    },
  });

  const noData = !loading && !error && data.getSchemaTableStats == null;

  let sizeTrend: React.ReactNode = "";
  if (stats && stats7dAgo) {
    const { dataSizeBytes: sizeBytes, snapshotAt } = stats;
    const { dataSizeBytes: sizeBytes7dAgo, snapshotAt: snapshotAt7dAgo } =
      stats7dAgo;
    if (sizeBytes7dAgo !== null && sizeBytes7dAgo !== 0 && sizeBytes !== null) {
      const trendContent = formatBytesTrend(sizeBytes, sizeBytes7dAgo);
      const trendTooltip = formatBytesTrendDetail(
        sizeBytes,
        sizeBytes7dAgo,
        snapshotAt,
        snapshotAt7dAgo,
      );

      sizeTrend = <Badge title={trendTooltip}>{trendContent}</Badge>;
    }
  }

  return (
    <Panel
      title={`${
        (materializedView && "Materialized View") || "Table"
      } Statistics`}
    >
      {stats && (
        <PanelTable horizontal borders>
          <tbody>
            <tr>
              <th>Data + TOAST Size</th>
              <td>
                {formatBytes(stats.dataSizeBytes)}
                {sizeTrend}
              </td>
              <th>Estimated Visible Rows</th>
              <td>{formatNumber(stats.liveTuples)}</td>
            </tr>
            <tr>
              <th>Size of Indexes</th>
              <td>{formatBytes(stats.indexSizeBytes)}</td>
              <th>Estimated Dead Rows</th>
              <td>{formatNumber(stats.deadTuples)}</td>
            </tr>
            <tr>
              <th>Total Size</th>
              <td>{formatBytes(stats.dataSizeBytes + stats.indexSizeBytes)}</td>
              {fillfactor && <th>Fillfactor</th>}
              {fillfactor && <td>{fillfactor}</td>}
            </tr>
            {partitionedBy && (
              <tr>
                <th>Partitioned By</th>
                <td colSpan={3}>{partitionedBy}</td>
              </tr>
            )}
          </tbody>
        </PanelTable>
      )}
      {!stats && (
        <PanelSection>
          <p>
            <strong>Error:</strong> This{" "}
            {(materializedView && "materialized view") || "table"} has limited
            statistics available.
          </p>
          <p>
            This sometimes happens after collector restarts, if this persists
            please contact support.
          </p>
        </PanelSection>
      )}
      <div className="grid grid-cols-2 border-t border-[#e8e8ee] pb-2">
        <GraphSection
          noData={noData}
          className={styles.tableGraphSection}
          loading={loading}
          error={error}
        >
          <h4>Table Size</h4>
          <DateRangeGraph
            data={data?.getSchemaTableStats as unknown as Data}
            axes={{ left: { format: formatBytes } }}
            series={[
              {
                key: "tableSize",
                type: AreaSeries,
                label: "Data Size",
              },
              {
                key: "toastSize",
                type: AreaSeries,
                label: "TOAST Size",
              },
              {
                key: "indexSize",
                type: AreaSeries,
                label: "Index Size",
              },
              {
                key: "totalSize",
                type: LineSeries,
                label: "Total Size",
              },
            ]}
          />
        </GraphSection>
        <GraphSection
          noData={noData}
          className={styles.tableGraphSection}
          loading={loading}
          error={error}
        >
          <h4>Table Activity</h4>
          <DateRangeGraph
            data={data?.getSchemaTableStats as unknown as Data}
            axes={{
              left: {
                format: "count",
                tipFormat: (y: number): string =>
                  format(",.2f")(y) + " rows / min",
              },
            }}
            series={[
              { key: "inserts", label: "Inserted" },
              { key: "updates", label: "Updated" },
              { key: "hotUpdates", label: "HOT Updated" },
              { key: "deletes", label: "Deleted" },
            ]}
          />
        </GraphSection>
        {data?.getSchemaTableStats?.cachedDataSize && (
          <GraphSection
            noData={noData}
            className={styles.tableGraphSection}
            loading={loading}
            error={error}
          >
            <h4>Cached Size</h4>
            <DateRangeGraph
              data={data?.getSchemaTableStats as unknown as Data}
              axes={{ left: { format: formatBytes } }}
              series={[
                {
                  key: "cachedDataSize",
                  type: AreaSeries,
                  label: "Data Size",
                },
                {
                  key: "cachedToastSize",
                  type: AreaSeries,
                  label: "TOAST Size",
                },
                {
                  key: "cachedIndexSize",
                  type: AreaSeries,
                  label: "Index Size",
                },
              ]}
            />
          </GraphSection>
        )}
      </div>
    </Panel>
  );
};

export default SchemaTableStats;
